import brew from "brew-js";
import $ from "jquery"
import waterpipe from "waterpipe";
import { combinePath, isSubPathOf, toSegments } from "brew-js/util/path";
import { DataView } from "zeta-dom-react";
import { FlyoutToggleMixin, MixinRef, navigateTo, redirectTo } from "brew-js-react";
import router from "brew-js/extension/router";
import scrollable from "brew-js/extension/scrollable";
import i18n from "brew-js/extension/i18n";
import viewport from "brew-js/extension/viewport";
import dom from "zeta-dom/dom";
import { define, delay, matchWord, noop, resolve } from "zeta-dom/util";
import api from "src/api";
import { CommonBlocking, MaintenanceDetail } from "src/api/types";
import config from "src/config";
import * as env from "src/config/env";
import { CLASSIC_EAC_URL, PUBLIC_URL } from "src/config/env";
import { translate } from "src/i18n";
import { assign } from "src/util/common";
import { Home, Login } from "src/views";
import { sendAnalytic } from "./analytics";
import { AuthContext } from "./auth";
import { navigationUtil } from "./navigation";
import { initServiceWorker } from "./serviceWorker";

interface UIToggles {
    notificationFlyout?: MixinRef<FlyoutToggleMixin>;
    searchFlyout?: MixinRef<FlyoutToggleMixin>;
    whatsNewFlyout?: MixinRef<FlyoutToggleMixin>;
}

interface Link {
    label: string;
    href: string;
}

const appContext = {
    auth: null as unknown as AuthContext,
    navigation: navigationUtil,
    maintenance: null as unknown as MaintenanceDetail,
    blockings: [] as CommonBlocking[],
    toggleFor: {} as UIToggles,
    mainOverlayOpened: false,
    mainNavigationOpened: false,
    headerBackLink: null as unknown as Link | null,
    sendAnalytic: sendAnalytic,
    get canViewMyPerformancePage() {
        if (!this.auth.currentUser?.staffLevel) {
            return !matchWord(this.auth.currentUser?.sCodeType || '', 'TM RM RE') && this.auth.currentUser?.userType !== 'assistant';
        }
        return this.auth.currentUser?.agentType !== 'staff' && Number(this.auth.currentUser?.staffLevel) > 4;
    },
    get canViewCampaigns() {
        return config.enableCampaigns && !matchWord(this.auth.currentUser?.sCodeType || '', 'AM');
    },
    get showReportLinksInForms() {
        return this.auth.currentUser?.userType === 'assistant';
    },
    get isAgent() {
        return !this.auth.currentUser?.staffLevel;
    },
    async handleError(error: Error) {
        return resolve().then(function next(): Promise<any> {
            // error must be handled at top level component
            // wait until the component is initialized if otherwise
            return dom.emit('error', dom.root, { error }) || delay(200).then(next);
        });
    },
    openSSOLink(requestURL: string, params?: object, returnURL?: string) {
        const param = {
            action: combinePath(CLASSIC_EAC_URL, '/ssoMode.do'),
            token: app.auth.accessToken,
            lang: app.language === 'en' ? 'en' : 'zh',
            cloudURL: returnURL || app.previousPath || app.route.getPath({ language: app.language }),
            requestURL: requestURL,
            params: params && JSON.stringify(params)
        };
        const form = $(waterpipe(`
            <form method="post" action="{{action}}" style="position:fixed;top:-10px;left;-10px">
                <input type="hidden" name="lang" value="{{lang}}" />
                <input type="hidden" name="requestURL" value="{{requestURL}}" />
                <input type="hidden" name="cloudURL" value="{{cloudURL}}" />
                <input type="hidden" name="params" value="{{params}}" />
                <input type="hidden" name="authorization" value="Bearer {{token}}" />
            </form>`, param)).appendTo(document.body)[0] as HTMLFormElement;
        form.submit();
    }
};

function onAppNavigate(e: Zeta.ZetaEventType<"navigate", Brew.RouterEventMap, Element>) {
    if (e.route.view === 'logout') {
        return app.auth.logout().then(() => {
            if (!config.enableLoginPage) {
                return new Promise(noop);
            }
        });
    }
    if (e.route.view === 'redirect') {
        if (!app.auth.isLoggedIn) {
            return redirectTo(Login);
        }
        const requestURL = e.route.requestURL as string;
        const linkUrlToken = config.menuConfig[requestURL]?.linkUrlToken;
        Promise.resolve().then(async () => {
            let link = await api.getLinkByID(requestURL);
            if (linkUrlToken) {
                window.location.replace(await api.replaceUrlToken(link!.url, linkUrlToken));
            } else {
                const url = link ? app.route.parse(link.url).requestURL : requestURL;
                const params = Object.fromEntries(new URLSearchParams(window.location.search).entries());
                app.openSSOLink(url, params, e.oldPathname);
            }
        }).catch(async (err) => {
            await app.handleError(err);
            navigateTo(Home);
        });
        return new Promise(noop);
    }
}

export const app = brew.with(router, scrollable, i18n, viewport, appContext)(app1 => {
    app1.useRouter({
        baseUrl: PUBLIC_URL,
        explicitBaseUrl: true,
        routes: config.routes
    });
    app1.useI18n({
        defaultLanguage: config.defaultLanguage,
        languages: config.languages,
        routeParam: 'language'
    });
    app1.useScrollable({
        scrollbarClass: 'scrollbar',
        scrollbarInset: 4
    });
    if (config.enableServiceWorker) {
        app1.beforeInit(initServiceWorker);
    }
    assign(app1, { auth: new AuthContext(app1) });

    app1.beforeInit(async () => {
        const res = await api.getIsSystemMaintenance();
        if (res.isMaintenance) {
            app1.maintenance = res;
            app1.navigate(app1.route.getPath({ view: 'login' }), true);
        }
    });

    app1.on('navigate', (e) => {
        const validPaths = ["/agency", "/agency/en", "/agency/zh-hk", "/agency/en/logout", "/agency/zh-hk/logout"];
        if (!window.opener) {
            if (!validPaths.includes(e.pathname)) {
                (async () => {
                    try {
                        //调用checkFRStatus检查登录状态
                        const response = await api.checkFRStatus();
                        if (response.status === 'inactive') {
                            // 登录状态无效，触发登出流程并取消当前请求
                            await app.auth.logoutSilent(true);
                            return Promise.reject(new Error('Session expired'));
                        }
                    } catch (error: any) {
                        // 触发登出流程
                        await app.auth.logoutSilent(true);
                        // 处理检查登录状态时的错误
                        return Promise.reject(error);
                    }
                })();
            }
        }
        return onAppNavigate(e);
    });

    app1.on('pageload', e => {
        if (app1.auth.isLoggedIn) {
            app1.sendAnalytic('onPageView', {
                hostname: window.location.host,
                pageTitle: toSegments(isSubPathOf(e.pathname, PUBLIC_URL) || '').slice(1).join(':'),
                agentId: app1.isAgent ? app1.auth.currentUser!.agentId : `**${app1.auth.currentUser!.agentId}`,
                agentRole: app1.isAgent ? app1.auth.currentUser!.agentType : "staff",
                app: app1.auth.currentUser!.loginMethod
            });
        }
    });

    app1.auth.on('login', e => {
        app1.blockings = [...e.user.blockings.map(v => ({ id: v }))];
    });

    app1.watch('language', lang => {
        dom.root.setAttribute('lang', lang);
    }, true);

    DataView.pageSize = config.pageSize;

    // expose environment variables and essential properties to
    // waterpipe templates
    assign(waterpipe.globals, env);
    define(waterpipe.globals, {
        get LANG() {
            return app1.language;
        },
        get MONTH_FORMAT() {
            return translate('common.monthFormat.pattern');
        },
        get DATE_FORMAT() {
            return translate('common.dateFormat.pattern');
        }
    });
});
